// Whitelabel-aware brand accent for use OUTSIDE Tailwind classes.
//
// useBranding writes the partner's color to the `--primary-color` CSS variable
// as an "R G B" triplet (see applyBranding in useBranding.js); the CG default
// lives in styles/main.css. Tailwind's `primaryColor` token already reads it —
// these helpers cover the two places classes can't reach:
//
//  - inline `style={{ ... }}` accents (progress fills, legend dots, accent
//    bars): use PRIMARY_COLOR / primaryColorAlpha — live CSS, retheme
//    automatically.
//  - canvas renderers (Chart.js datasets, gradients): CSS variables are NOT
//    resolved inside a canvas context, so use resolvedPrimaryColor() to get a
//    concrete rgba() string at render time.
//
// Never hardcode the brand green (#00B894 / the old hand-picked #1D9E75) —
// that's what broke whitelabel styling. Semantic status colors (amber
// warnings, red errors, blue info) are NOT brand accents and stay hardcoded.

/**
 * Normalize a hex color to lower-case `#rrggbb`, accepting an optional leading
 * `#` and any case (the widget URL param historically carries it WITHOUT the
 * `#`). Returns null for anything that isn't a 6-digit hex — callers fall
 * through to the next precedence tier (D50).
 */
export function normalizeHexColor(value) {
  if (!value) return null;
  const hex = String(value).replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex.toLowerCase()}` : null;
}

/**
 * hex → `"r g b"` triplet for setting a `--primary-color` / `--primary-color-dark`
 * CSS variable (e.g. theming a public page — or :root, so portaled dialogs
 * inherit — to the supplier's chosen color), matching the space-separated format
 * applyBranding writes. Returns null for bad input.
 */
export function hexToRgbTriplet(hex) {
  const norm = normalizeHexColor(hex);
  if (!norm) return null;
  return [
    parseInt(norm.slice(1, 3), 16),
    parseInt(norm.slice(3, 5), 16),
    parseInt(norm.slice(5, 7), 16),
  ].join(' ');
}

/**
 * Darken a hex toward black by `factor` (0 = black, 1 = unchanged) — mirrors
 * useBranding's `computeDarker` so a supplier's derived `--primary-color-dark`
 * matches how the deployment brand's dark accent is computed. Returns
 * lower-case `#rrggbb`, or null for bad input.
 */
export function darkenHexColor(hex, factor = 0.7) {
  const norm = normalizeHexColor(hex);
  if (!norm) return null;
  const channel = (start) =>
    Math.max(0, Math.floor(parseInt(norm.slice(start, start + 2), 16) * factor))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * Mix a hex toward white (0 = unchanged, 1 = white), for surfaces that need a concrete
 * tint without a CSS variable or alpha (PDFs). Returns lower-case `#rrggbb`, or null for
 * bad input.
 */
export function tintHexColor(hex, weight = 0.4) {
  const norm = normalizeHexColor(hex);
  if (!norm) return null;
  const channel = (start) => {
    const value = parseInt(norm.slice(start, start + 2), 16);
    return Math.round(value + (255 - value) * weight)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * The accent's CSS-variable pair as a React `style` object, for surfaces that
 * theme a scoped subtree declaratively (the public widget root). Keeps "which
 * two variables, and how the dark one is derived" in one place — the imperative
 * `applyAccentColor` below writes exactly the same pair.
 *
 * Returns an empty object for an invalid colour so it can be spread safely.
 */
export function accentCssVars(hex) {
  const primaryTriplet = hexToRgbTriplet(hex);
  if (!primaryTriplet) return {};
  const darkTriplet = hexToRgbTriplet(darkenHexColor(hex));
  return {
    '--primary-color': primaryTriplet,
    ...(darkTriplet ? { '--primary-color-dark': darkTriplet } : {}),
  };
}

/**
 * Apply a supplier/operator accent to a DOM element's CSS variables, returning
 * a cleanup function.
 *
 * The public share page and the public widget page both need this: theme the
 * surface to the portal owner's chosen colour, then restore the deployment
 * brand when the colour changes or the component unmounts. Both had their own
 * copy of the `hexToRgbTriplet` + `darkenHexColor` + `setProperty` dance.
 *
 * Pass `document.documentElement` (the share page does) so portaled dialogs —
 * which render to document.body, outside any wrapper — inherit the accent too;
 * pass a scoped node (the widget does) to theme only that subtree.
 *
 * @param {HTMLElement|null} element Target node; a nullish element is a no-op.
 * @param {string|null} hex          Accent colour; nullish/invalid is a no-op.
 * @param {() => void} [restore]     Cleanup, e.g. `useBranding.getState().applyCss`.
 * @returns {() => void} cleanup
 */
export function applyAccentColor(element, hex, restore) {
  const primaryTriplet = hexToRgbTriplet(hex);
  if (!element || !primaryTriplet) return () => {};
  const darkTriplet = hexToRgbTriplet(darkenHexColor(hex));
  element.style.setProperty('--primary-color', primaryTriplet);
  if (darkTriplet) element.style.setProperty('--primary-color-dark', darkTriplet);
  return () => {
    restore?.();
  };
}

/** Live CSS color string for DOM inline styles. */
export const PRIMARY_COLOR = 'rgb(var(--primary-color))';

/** Live CSS color with alpha, for DOM inline styles. */
export const primaryColorAlpha = (alpha) => `rgb(var(--primary-color) / ${alpha})`;

// CG default triplet — fallback for non-browser contexts (tests) or before
// branding applies. Matches --primary-color in styles/main.css.
const CG_DEFAULT_TRIPLET = [0, 184, 148];

/**
 * Concrete `rgba(r, g, b, a)` string resolved from the CSS variable — for
 * canvas contexts (Chart.js) that can't consume CSS variables. Resolve at
 * render time so a runtime brand (e.g. Faradai hostname match) is picked up.
 */
export function resolvedPrimaryColor(alpha = 1) {
  const [r, g, b] = resolveTriplet();
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Brands whose primary is too dark to read as an accent on a near-black
// surface, mapped to the accent those surfaces use instead. Crowe's primary
// against the carbon-price tooltip background (rgba(17,24,39,0.95)) is barely
// legible — and it vanishes entirely in the auth panel's globe illustration —
// so it gets white. o2b's #176348 measures about 2.6:1 against the auth panel,
// so it gets white too. `tiba` is the reusable demo slot on
// whitelabel.testcg.co.uk, which currently demos o2b's palette; recheck this
// entry when the slot is rebranded. ChamberCustoms' primary, Mid Green #065465,
// is equally lost there; its guidelines (v01 p.5) name Bright Green as the
// accent and allow it on their dark greens. Keyed by REACT_APP_WHITELABEL
// (= the customer id in the backend registry).
const DARK_SURFACE_ACCENTS = {
  crowe: '#ffffff',
  o2b: '#ffffff',
  tiba: '#ffffff',
  chambercustoms: '#0de8c2',
};

/**
 * The hex accent this brand uses on a near-black surface in place of its
 * primary, or null when the primary itself works there (see the map above).
 * Pass the runtime brand key (useBranding's `key`); defaults to the build-time
 * whitelabel id for non-React callers.
 */
export function darkSurfaceAccent(brandKey = process.env.REACT_APP_WHITELABEL || 'carbonglance') {
  return DARK_SURFACE_ACCENTS[String(brandKey).toLowerCase()] ?? null;
}

/**
 * Primary accent for text drawn ON a dark surface (chart tooltips). Identical
 * to resolvedPrimaryColor() for every brand except those mapped above.
 */
export function resolvedPrimaryColorOnDark(alpha = 1) {
  const accent = darkSurfaceAccent();
  if (accent) return `rgb(${hexToRgbTriplet(accent)} / ${alpha})`;
  return resolvedPrimaryColor(alpha);
}

/**
 * Opaque lighter tint of the primary (channel-mixed toward white), for canvas
 * fills where alpha would let gridlines bleed through (e.g. stacked bars).
 * `weight` 0 = the primary itself, 1 = white.
 */
export function resolvedPrimaryTint(weight = 0.4) {
  const [r, g, b] = resolveTriplet().map((c) => Math.round(c + (255 - c) * weight));
  return `rgb(${r}, ${g}, ${b})`;
}

function resolveTriplet() {
  if (typeof document !== 'undefined') {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color')
      .trim();
    const parts = raw.split(/\s+/).map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) return parts;
  }
  return CG_DEFAULT_TRIPLET;
}
